import { existsSync, promises as fs } from "fs";
import path from "path";
import {
  DEFAULT_COMPONENTS,
  getConfig,
  rawConfigSchema,
  resolveConfigPaths,
  type Config,
} from "@/src/utils/get-config";
import { getPackageManager } from "@/src/utils/get-package-manager";
import { handleError } from "@/src/utils/handle-error";
import { logger } from "@/src/utils/logger";
import chalk from "chalk";
import { Command } from "commander";
import ora from "ora";
import prompts from "prompts";
import { z } from "zod";
import { execa } from "execa";

const initOptionsSchema = z.object({
  cwd: z.string(),
  yes: z.boolean(),
  defaults: z.boolean(),
});

export const init = new Command()
  .name("init")
  .description("initialize uilabs in your project")
  .option("-y, --yes", "skip confirmation prompt.", false)
  .option("-d, --defaults,", "use default configuration.", false)
  .option(
    "-c, --cwd <cwd>",
    "the working directory. defaults to the current directory.",
    process.cwd(),
  )
  .action(async (opts) => {
    try {
      const options = initOptionsSchema.parse(opts);
      const cwd = path.resolve(options.cwd);

      // Ensure target directory exists.
      if (!existsSync(cwd)) {
        logger.error(`The path ${cwd} does not exist. Please try again.`);
        process.exit(1);
      }

      // Read config.
      const existingConfig = await getConfig(cwd);
      const config = await promptForConfig(cwd, existingConfig, options.yes);
      await runInit(cwd, config);

      logger.info("");
      logger.info(
        `${chalk.green(
          "Success!",
        )} Project initialization completed. You may now add components.`,
      );
      logger.info("");
    } catch (error) {
      handleError(error);
    }
  });

export async function promptForConfig(
  cwd: string,
  defaultConfig: Config | null = null,
  skip = false,
) {
  const highlight = (text: string) => chalk.cyan(text);

  const options = await prompts([
    {
      type: "toggle",
      name: "typescript",
      message: `Would you like to use ${highlight("TypeScript")}?`,
      initial: defaultConfig?.tsx ?? true,
      active: "yes",
      inactive: "no",
    },
    {
      type: "text",
      name: "components",
      message: `Configure the import alias for ${highlight("components")}:`,
      initial: defaultConfig?.aliases["components"] ?? DEFAULT_COMPONENTS,
    },
    {
      type: "toggle",
      name: "utils",
      message: `Would you like to install ${highlight(
        "@uilabs/utils",
      )} package?`,
      initial: true,
      active: "yes",
      inactive: "no",
    },
    {
      type: "toggle",
      name: "rsc",
      message: `Are you using ${highlight("React Server Components")}?`,
      initial: defaultConfig?.rsc ?? true,
      active: "yes",
      inactive: "no",
    },
  ]);

  const config = rawConfigSchema.parse({
    $schema: "https://uilabs.app/schema.json",
    rsc: options.rsc,
    tsx: options.typescript,
    aliases: {
      components: options.components,
    },
  });

  if (!skip) {
    const { proceed } = await prompts({
      type: "confirm",
      name: "proceed",
      message: `Write configuration to ${highlight(
        "uilabs.config.json",
      )}. Proceed?`,
      initial: true,
    });

    if (!proceed) {
      process.exit(0);
    }
  }

  // Write to file.
  logger.info("");
  const spinner = ora(`Writing uilabs.config.json...`).start();
  const targetPath = path.resolve(cwd, "uilabs.config.json");
  await fs.writeFile(targetPath, JSON.stringify(config, null, 2), "utf8");
  spinner.succeed();

  if (options.utils) {
    // Install dependencies.
    const dependenciesSpinner = ora(
      `Installing ${highlight("@uilabs/utils")}...`,
    )?.start();
    const packageManager = await getPackageManager(cwd);

    await execa(
      packageManager,
      [packageManager === "npm" ? "install" : "add", "@uilabs/utils"],
      {
        cwd,
      },
    );
    dependenciesSpinner?.succeed();
  }

  return await resolveConfigPaths(cwd, config);
}

export async function runInit(cwd: string, config: Config) {
  const spinner = ora(`Initializing project...`)?.start();

  // Ensure all resolved paths directories exist.
  for (const [key, resolvedPath] of Object.entries(config.resolvedPaths)) {
    // Cast resolvedPath to string to satisfy TypeScript
    const resolvedPathStr = resolvedPath as string;

    // Determine if the path is a file or directory.
    let dirname = path.extname(resolvedPathStr)
      ? path.dirname(resolvedPathStr)
      : resolvedPathStr;

    if (!existsSync(dirname)) {
      await fs.mkdir(dirname, { recursive: true });
    }
  }

  spinner?.succeed();
}
